
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Users, Search, Link, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface KolRecord {
  id: string;
  creator_name: string;
  social_media_handler: string;
  social_media_type: string;
  parent_id: string | null;
}

interface ManualLinkingControlsProps {
  onLinkRecords: (parentId: string, childId: string) => void;
  isProcessing: boolean;
}

export const ManualLinkingControls: React.FC<ManualLinkingControlsProps> = ({
  onLinkRecords,
  isProcessing
}) => {
  const [availableRecords, setAvailableRecords] = useState<KolRecord[]>([]);
  const [selectedParent, setSelectedParent] = useState<KolRecord | null>(null);
  const [selectedChild, setSelectedChild] = useState<KolRecord | null>(null);
  const [parentOpen, setParentOpen] = useState(false);
  const [childOpen, setChildOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchAvailableRecords();
  }, []);

  const fetchAvailableRecords = async () => {
    try {
      const { data, error } = await supabase
        .from('kol_db')
        .select('id, creator_name, social_media_handler, social_media_type, parent_id')
        .order('creator_name');

      if (error) throw error;
      setAvailableRecords(data || []);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast({
        title: "Error",
        description: "Failed to fetch available records",
        variant: "destructive"
      });
    }
  };

  const handleLinkRecords = () => {
    if (!selectedParent || !selectedChild) {
      toast({
        title: "Error",
        description: "Please select both parent and child records",
        variant: "destructive"
      });
      return;
    }

    if (selectedParent.id === selectedChild.id) {
      toast({
        title: "Error",
        description: "A record cannot be its own parent",
        variant: "destructive"
      });
      return;
    }

    // Check if child is already a parent of the selected parent (prevent circular reference)
    const wouldCreateCircularRef = checkCircularReference(selectedChild.id, selectedParent.id);
    if (wouldCreateCircularRef) {
      toast({
        title: "Error",
        description: "This would create a circular reference",
        variant: "destructive"
      });
      return;
    }

    onLinkRecords(selectedParent.id, selectedChild.id);
    
    // Clear selections after linking
    setSelectedParent(null);
    setSelectedChild(null);
    fetchAvailableRecords(); // Refresh data
  };

  const checkCircularReference = (childId: string, parentId: string): boolean => {
    // This is a simplified check - in a real implementation, you'd want to 
    // traverse the entire hierarchy to check for circular references
    const parentRecord = availableRecords.find(r => r.id === parentId);
    return parentRecord?.parent_id === childId;
  };

  const getAvailableParents = () => {
    return availableRecords.filter(record => {
      // Exclude records that are already children
      const isCurrentChild = selectedChild && record.id === selectedChild.id;
      return !isCurrentChild;
    });
  };

  const getAvailableChildren = () => {
    return availableRecords.filter(record => {
      // Exclude records that are already parents of other records
      const isCurrentParent = selectedParent && record.id === selectedParent.id;
      return !isCurrentParent;
    });
  };

  const filteredRecords = availableRecords.filter(record =>
    record.creator_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.social_media_handler.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.social_media_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900 dark:text-amber-100">
                Manual Linking Instructions
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Select a parent record and a child record, then click "Link Records" to establish a parent-child relationship. 
                The system will prevent circular references and duplicate relationships.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Records
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Search by creator name, handler, or platform..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
          {searchTerm && (
            <div className="mt-2 text-sm text-muted-foreground">
              Found {filteredRecords.length} matching records
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Manual Linking
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Parent Selection */}
            <div className="space-y-2">
              <Label htmlFor="parent-select">Select Parent Record</Label>
              <Popover open={parentOpen} onOpenChange={setParentOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={parentOpen}
                    className="w-full justify-between"
                  >
                    {selectedParent
                      ? `${selectedParent.creator_name} (${selectedParent.social_media_type})`
                      : "Select parent record..."}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search parent records..." />
                    <CommandList>
                      <CommandEmpty>No records found.</CommandEmpty>
                      <CommandGroup>
                        {getAvailableParents().map((record) => (
                          <CommandItem
                            key={record.id}
                            value={`${record.creator_name} ${record.social_media_handler} ${record.social_media_type}`}
                            onSelect={() => {
                              setSelectedParent(record);
                              setParentOpen(false);
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{record.creator_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {record.social_media_handler} • {record.social_media_type}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedParent && (
                <Badge variant="secondary" className="w-fit">
                  Selected: {selectedParent.creator_name}
                </Badge>
              )}
            </div>

            {/* Child Selection */}
            <div className="space-y-2">
              <Label htmlFor="child-select">Select Child Record</Label>
              <Popover open={childOpen} onOpenChange={setChildOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={childOpen}
                    className="w-full justify-between"
                  >
                    {selectedChild
                      ? `${selectedChild.creator_name} (${selectedChild.social_media_type})`
                      : "Select child record..."}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search child records..." />
                    <CommandList>
                      <CommandEmpty>No records found.</CommandEmpty>
                      <CommandGroup>
                        {getAvailableChildren().map((record) => (
                          <CommandItem
                            key={record.id}
                            value={`${record.creator_name} ${record.social_media_handler} ${record.social_media_type}`}
                            onSelect={() => {
                              setSelectedChild(record);
                              setChildOpen(false);
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{record.creator_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {record.social_media_handler} • {record.social_media_type}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedChild && (
                <Badge variant="secondary" className="w-fit">
                  Selected: {selectedChild.creator_name}
                </Badge>
              )}
            </div>
          </div>

          {/* Link Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleLinkRecords}
              disabled={!selectedParent || !selectedChild || isProcessing}
              size="lg"
              className="min-w-32"
            >
              <Link className="h-4 w-4 mr-2" />
              {isProcessing ? 'Processing...' : 'Link Records'}
            </Button>
          </div>

          {/* Preview */}
          {selectedParent && selectedChild && (
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Link Preview
                </h4>
                <div className="text-sm space-y-1">
                  <div><strong>Parent:</strong> {selectedParent.creator_name} ({selectedParent.social_media_type})</div>
                  <div><strong>Child:</strong> {selectedChild.creator_name} ({selectedChild.social_media_type})</div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
